import { ref, watch, type Ref } from 'vue'

interface SyncedModel<TModel extends object> {
  model: Ref<TModel>
  confirm: (savedPatch: Partial<TModel>) => void
}

export function useSyncedModel<TModel extends object>(serverModel: () => TModel): SyncedModel<TModel> {
  let confirmed = serverModel()
  const model = ref({ ...confirmed }) as Ref<TModel>

  watch(serverModel, (server) => {
    const unconfirmedKeys = (Object.keys(server) as (keyof TModel)[]).filter(
      (key) => model.value[key] !== confirmed[key],
    )
    const keep = (source: TModel): Partial<TModel> =>
      Object.fromEntries(unconfirmedKeys.map((key) => [key, source[key]])) as Partial<TModel>

    model.value = { ...server, ...keep(model.value) }
    confirmed = { ...server, ...keep(confirmed) }
  })

  const confirm = (savedPatch: Partial<TModel>): void => {
    confirmed = { ...confirmed, ...savedPatch }
  }

  return { model, confirm }
}
